/**
 * ProposalList - Active and completed governance proposals
 * 
 * Displays all governance actions with:
 * - Status badges (pending, approved, rejected, expired)
 * - Type indicators (circuit, config, identity, governance)
 * - Signature progress
 * - Timelock countdowns
 * - PoVC proof badges
 * 
 * Part of #260 Governance Hub
 */

import { useState } from 'react';
import SignatureProgress from './SignatureProgress';
import TimelockCountdown from './TimelockCountdown';
import { SparkRenderer } from './SparkRenderer';
import './ProposalList.css';

// SVG Icons
const Icons = {
  Circuit: () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="8" cy="8" r="3" />
      <path d="M8 1v4M8 11v4M1 8h4M11 8h4" />
    </svg>
  ),
  Config: () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="8" cy="8" r="2" />
      <path d="M8 1v2M8 13v2M1 8h2M13 8h2M3 3l1.5 1.5M11.5 11.5L13 13M3 13l1.5-1.5M11.5 4.5L13 3" />
    </svg>
  ),
  Identity: () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="8" cy="5" r="3" />
      <path d="M2 14c0-3 2.5-4.5 6-4.5s6 1.5 6 4.5" />
    </svg>
  ),
  Governance: () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M8 1l6 3v4c0 4-2.5 6-6 8-3.5-2-6-4-6-8V4l6-3z" />
    </svg>
  ),
  Node: () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="4" y="4" width="8" height="8" rx="1" />
      <circle cx="4" cy="4" r="1.5" fill="currentColor" />
      <circle cx="12" cy="4" r="1.5" fill="currentColor" />
      <circle cx="4" cy="12" r="1.5" fill="currentColor" />
      <circle cx="12" cy="12" r="1.5" fill="currentColor" />
    </svg>
  ),
  Key: () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="5" cy="5" r="3" />
      <path d="M7 7l7 7M11 11l2 2M11 14l2-2" />
    </svg>
  ),
  Emergency: () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M8 1l6 10H2L8 1z" />
      <path d="M8 6v3M8 11h.01" />
    </svg>
  ),
  Release: () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M8 1v10M4 7l4 4 4-4" />
      <path d="M2 11v2a1 1 0 001 1h10a1 1 0 001-1v-2" />
    </svg>
  ),
  PoVC: () => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M7 1l5 3v4c0 3-2 4.5-5 5.5-3-1-5-2.5-5-5.5V4l5-3z" />
      <path d="M5 7l1.5 1.5L9 5.5" />
    </svg>
  ),
  Expand: () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 6l4 4 4-4" />
    </svg>
  ),
  Sign: () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M12 2l2 2-7 7-3 1 1-3 7-7z" />
    </svg>
  ),
};

export interface Proposal {
  id: string;
  type: 'circuit' | 'config' | 'identity' | 'governance' | 'node' | 'key' | 'emergency' | 'release';
  action: string;
  title: string;
  description?: string;
  status: 'pending' | 'approved' | 'rejected' | 'expired' | 'executing' | 'completed';
  initiator: string;
  createdAt: string;
  expiresAt?: string;
  timelockEndsAt?: string;
  threshold: 'normal' | 'critical';
  requiredSignatures: number;
  currentSignatures: number;
  signatures: {
    signer: string;
    signedAt: string;
    walletId: string;
  }[];
  povcProof?: {
    hash: string;
    timestamp: string;
    verified: boolean;
  };
}

interface ProposalListProps {
  proposals: Proposal[];
  normalThreshold: number;
  criticalThreshold: number;
  keyHolderCount: number;
  onSign?: (proposal: Proposal) => void;
  onViewDetails?: (proposal: Proposal) => void;
  loading?: boolean;
}

export default function ProposalList({
  proposals,
  normalThreshold,
  criticalThreshold,
  keyHolderCount,
  onSign,
  onViewDetails,
  loading = false,
}: ProposalListProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [signingProposal, setSigningProposal] = useState<Proposal | null>(null);

  const getTypeIcon = (type: Proposal['type']) => {
    switch (type) {
      case 'circuit': return <Icons.Circuit />;
      case 'config': return <Icons.Config />;
      case 'identity': return <Icons.Identity />;
      case 'governance': return <Icons.Governance />;
      case 'node': return <Icons.Node />;
      case 'key': return <Icons.Key />;
      case 'emergency': return <Icons.Emergency />;
      case 'release': return <Icons.Release />;
      default: return <Icons.Governance />;
    }
  };

  const getStatusClass = (status: Proposal['status']) => {
    switch (status) {
      case 'approved':
      case 'completed': return 'success';
      case 'rejected':
      case 'expired': return 'error';
      case 'executing': return 'warning';
      default: return 'pending';
    }
  };

  const getActionLabel = (action: string): string => {
    const labels: Record<string, string> = {
      'governance.node.deploy': 'Deploy Node',
      'governance.node.decommission': 'Decommission Node',
      'governance.release.deploy': 'Deploy Release',
      'governance.circuit.deploy': 'Deploy Circuit',
      'governance.config.update': 'Update Config',
      'governance.key.add': 'Add Key Holder',
      'governance.key.rotate': 'Rotate Key',
      'governance.key.remove': 'Remove Key Holder',
      'governance.threshold.update': 'Update Thresholds',
      'governance.emergency.halt': 'Emergency Halt',
      'governance.emergency.resume': 'Resume Operations',
    };
    return labels[action] || action;
  };

  const formatTimeAgo = (timestamp: string) => {
    const seconds = Math.floor((Date.now() - new Date(timestamp).getTime()) / 1000);
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  const handleSign = (proposal: Proposal) => {
    setSigningProposal(proposal);
    onSign?.(proposal);
  };

  const handleCloseSign = () => {
    setSigningProposal(null);
  };

  if (loading) {
    return (
      <div className="proposal-list loading">
        <div className="spinner" />
        <span>Loading proposals...</span>
      </div>
    );
  }

  if (proposals.length === 0) {
    return (
      <div className="proposal-list empty">
        <Icons.Governance />
        <p>No governance proposals</p>
        <span>Create a new proposal to get started</span>
      </div>
    );
  }

  return (
    <div className="proposal-list">
      {proposals.map((proposal) => {
        const isExpanded = expandedId === proposal.id;
        const requiredSigs = proposal.threshold === 'critical' ? criticalThreshold : normalThreshold;
        const isPending = proposal.status === 'pending';
        const hasTimelock = proposal.timelockEndsAt && proposal.status === 'executing';

        return (
          <div 
            key={proposal.id} 
            className={`proposal-item ${isExpanded ? 'expanded' : ''} ${proposal.status}`}
          >
            {/* Main Row */}
            <div className="proposal-row" onClick={() => setExpandedId(isExpanded ? null : proposal.id)}>
              {/* Type Icon */}
              <div className={`proposal-type-icon ${proposal.type}`}>
                {getTypeIcon(proposal.type)}
              </div>

              {/* Content */}
              <div className="proposal-content">
                <div className="proposal-header">
                  <span className="proposal-title">{proposal.title}</span>
                  <span className={`proposal-status ${getStatusClass(proposal.status)}`}>
                    {proposal.status}
                  </span>
                </div>
                <div className="proposal-meta">
                  <span className="proposal-action">{getActionLabel(proposal.action)}</span>
                  <span className="proposal-separator">•</span>
                  <span className="proposal-initiator">by {proposal.initiator}</span>
                  <span className="proposal-separator">•</span>
                  <span className="proposal-time">{formatTimeAgo(proposal.createdAt)}</span>
                  {proposal.povcProof?.verified && (
                    <>
                      <span className="proposal-separator">•</span>
                      <span className="povc-badge" title={`PoVC: ${proposal.povcProof.hash.slice(0, 16)}...`}>
                        <Icons.PoVC /> PoVC
                      </span>
                    </>
                  )}
                </div>

                {/* Signature Progress (inline for pending) */}
                {isPending && (
                  <div className="proposal-progress-inline">
                    <SignatureProgress
                      current={proposal.currentSignatures}
                      required={requiredSigs}
                      total={keyHolderCount}
                      threshold={proposal.threshold}
                      compact
                    />
                  </div>
                )}

                {/* Timelock (inline for executing) */}
                {hasTimelock && (
                  <div className="proposal-timelock-inline">
                    <TimelockCountdown 
                      endsAt={proposal.timelockEndsAt!} 
                      compact 
                    />
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="proposal-actions">
                {isPending && (
                  <button 
                    className="btn-sign"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSign(proposal);
                    }}
                  >
                    <Icons.Sign /> Sign
                  </button>
                )}
                <button className={`btn-expand ${isExpanded ? 'expanded' : ''}`}>
                  <Icons.Expand />
                </button>
              </div>
            </div>

            {/* Expanded Details */}
            {isExpanded && (
              <div className="proposal-details">
                {proposal.description && (
                  <div className="detail-section">
                    <h4>Description</h4>
                    <p>{proposal.description}</p>
                  </div>
                )}

                <div className="detail-section">
                  <h4>Signature Progress</h4>
                  <SignatureProgress
                    current={proposal.currentSignatures}
                    required={requiredSigs}
                    total={keyHolderCount}
                    threshold={proposal.threshold}
                    signatures={proposal.signatures}
                  />
                </div>

                {proposal.timelockEndsAt && (
                  <div className="detail-section">
                    <h4>Timelock</h4>
                    <TimelockCountdown endsAt={proposal.timelockEndsAt} />
                  </div>
                )}

                {proposal.povcProof && (
                  <div className="detail-section">
                    <h4>PoVC Proof</h4>
                    <div className="povc-details">
                      <div className="povc-row">
                        <span className="povc-label">Hash:</span>
                        <code className="povc-hash">{proposal.povcProof.hash}</code>
                      </div>
                      <div className="povc-row">
                        <span className="povc-label">Timestamp:</span>
                        <span>{new Date(proposal.povcProof.timestamp).toLocaleString()}</span>
                      </div>
                      <div className="povc-row">
                        <span className="povc-label">Status:</span>
                        <span className={`povc-status ${proposal.povcProof.verified ? 'verified' : 'unverified'}`}>
                          {proposal.povcProof.verified ? 'Verified' : 'Unverified'}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="detail-actions">
                  <button 
                    className="btn-secondary"
                    onClick={() => onViewDetails?.(proposal)}
                  >
                    View Full Details
                  </button>
                  {isPending && (
                    <button 
                      className="btn-primary"
                      onClick={() => handleSign(proposal)}
                    >
                      <Icons.Sign /> Sign Proposal
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Signing Modal */}
      {signingProposal && (
        <div className="signing-modal-overlay" onClick={handleCloseSign}>
          <div className="signing-modal" onClick={(e) => e.stopPropagation()}>
            <div className="signing-modal-header">
              <h3>Sign Proposal</h3>
              <button className="modal-close" onClick={handleCloseSign}>×</button>
            </div>
            <div className="signing-modal-body">
              <p className="signing-title">{signingProposal.title}</p>
              <p className="signing-action">{getActionLabel(signingProposal.action)}</p>
              <div className="signing-spark">
                <p>Scan with your Seeker app to sign:</p>
                <SparkRenderer
                  challengeJson={JSON.stringify({
                    challenge_id: `sign-${signingProposal.id}`,
                    session_id: `session-${Date.now()}`,
                    display_code: signingProposal.id.slice(0, 8).toUpperCase(),
                    expires_at: Date.now() + 300000,
                    payload: btoa(JSON.stringify({
                      proposal_id: signingProposal.id,
                      action: signingProposal.action,
                    })),
                  })}
                  variant="genesis"
                  size={260}
                  showCode
                />
              </div>
            </div>
            <div className="signing-modal-footer">
              <button className="btn-secondary" onClick={handleCloseSign}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
